
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useSettings } from '@/context/SettingsContext';
import { PerceptiveSystemStat } from '@/hooks/usePerceptiveSystemStats';
import RpgPerceptiveSystemIcon from './RpgPerceptiveSystemIcon';

interface RpgPerceptiveSystemStatProps {
  stat: PerceptiveSystemStat;
}

const RpgPerceptiveSystemStatComponent: React.FC<RpgPerceptiveSystemStatProps> = ({ stat }) => {
  const { darkMode } = useSettings();

  const getRankName = (level: number): string => {
    if (level >= 10) return 'Grandmaster';
    if (level >= 8) return 'Master';
    if (level >= 6) return 'Expert';
    if (level >= 4) return 'Adept';
    if (level >= 2) return 'Apprentice';
    return 'Novice';
  };

  const getRankColor = (level: number): string => {
    if (level >= 10) return 'text-purple-300 border-purple-400/60 bg-purple-900/50';
    if (level >= 8) return 'text-yellow-300 border-yellow-400/60 bg-yellow-900/50';
    if (level >= 6) return 'text-blue-300 border-blue-400/60 bg-blue-900/50';
    if (level >= 4) return 'text-green-300 border-green-400/60 bg-green-900/50';
    if (level >= 2) return 'text-orange-300 border-orange-400/60 bg-orange-900/50';
    return 'text-gray-300 border-gray-400/60 bg-gray-900/50';
  };

  const getProgressColor = (level: number): string => {
    if (level >= 10) return 'bg-purple-500';
    if (level >= 8) return 'bg-yellow-500';
    if (level >= 6) return 'bg-blue-500';
    if (level >= 4) return 'bg-green-500';
    if (level >= 2) return 'bg-orange-500';
    return 'bg-gray-500';
  };

  const getElementalGradient = (systemId: number): string => {
    switch (systemId) {
      case 1: return 'from-blue-900/40 to-blue-800/50'; // Water
      case 2: return 'from-red-900/40 to-red-800/50'; // Fire
      case 3: return 'from-yellow-900/40 to-yellow-800/50'; // Lightning
      case 4: return 'from-purple-900/40 to-purple-800/50'; // Arcane
      case 5: return 'from-green-900/40 to-green-800/50'; // Nature
      case 6: return 'from-orange-900/40 to-orange-800/50'; // Light
      default: return 'from-gray-900/40 to-gray-800/50';
    }
  };

  return (
    <Card className={cn(
      "transition-all hover:shadow-lg hover:scale-105 border-2 relative overflow-hidden font-montserrat",
      `bg-gradient-to-br ${getElementalGradient(stat.systemId)}`,
      darkMode 
        ? "border-amber-600/40 shadow-amber-900/30" 
        : "border-amber-400/40 shadow-amber-600/30"
    )}>
      {/* Magical shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent -skew-x-12 animate-pulse opacity-30" />
      
      <CardContent className="p-4 relative z-10">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <RpgPerceptiveSystemIcon 
              diceValue={stat.systemId} 
              className="w-10 h-10 drop-shadow-md"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h4 className={cn(
                "text-sm font-bold truncate drop-shadow-sm",
                darkMode ? "text-amber-100" : "text-amber-900"
              )}>
                {stat.systemName}
              </h4>
              <Badge 
                variant="outline" 
                className={cn(
                  "text-xs border font-semibold px-2 py-1",
                  getRankColor(stat.level)
                )}
              >
                {getRankName(stat.level)}
              </Badge>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm font-medium">
                <span className={cn(
                  "drop-shadow-sm",
                  darkMode ? "text-amber-200" : "text-amber-800"
                )}>
                  Level {stat.level}
                </span>
                <span className={cn(
                  "drop-shadow-sm",
                  darkMode ? "text-amber-300" : "text-amber-700"
                )}>
                  {Math.round(stat.progressToNext)}% to next
                </span>
              </div>
              
              <Progress 
                value={stat.progressToNext} 
                className="h-2 bg-amber-900/60"
                style={{
                  '--progress-background': getProgressColor(stat.level)
                } as React.CSSProperties}
              />
              
              <div className={cn(
                "flex justify-between text-sm font-medium drop-shadow-sm",
                darkMode ? "text-amber-200/90" : "text-amber-800/90"
              )}>
                <span>{stat.totalEntries} quests</span>
                <span>{Math.round(stat.successRate)}% success</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RpgPerceptiveSystemStatComponent;
